import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/machines/api/config";
import getEvents from "@/machines/api/requests/events.requests";
import type { IGetEventsInput } from "@/machines/api/requests/events.requests";
import { useEventBufferStore, type TimeRange } from "@/machines/stores/useEventBufferStore";
import type { MachineEvent } from "@/machines/types/event";
import type { ApiResponse } from "@/shared/types/api";
import { useGetEvents, type IEventQueryOptions } from "@/machines/api/queries/events.queries";

/**
 * The live timeline's events, from either the whole window or a tail + buffer.
 *
 * ONE hook for every live consumer — `main-preview`, the map's per-machine
 * provider, the row's newest-event observer and the stack's freshness chip —
 * because they already share a query key on purpose (one request, four
 * observers) and must keep sharing whichever way they fetch. Swapping one of
 * them alone leaves the old full-window poll running and adds a second path on
 * top, which is strictly worse.
 *
 * Which path runs is a RUNTIME flag rather than a build-time one, so an A/B can
 * interleave both arms in a single session instead of comparing across rebuilds
 * on a site whose activity changes by the minute.
 */

const BUFFER_FLAG_KEY = "maestro-event-buffer";

/**
 * Read once per session. Mid-session switching would leave one path's data in
 * the buffer and the other's in the query cache, and every measurement after
 * the switch would describe neither.
 */
const readFlag = (): boolean => {
  try {
    return localStorage.getItem(BUFFER_FLAG_KEY) === "on";
  } catch {
    return false;
  }
};
const EVENT_BUFFER_ENABLED = readFlag();

/** How far back the frequent poll asks for — past the observed 82 s worst-case event lag. */
export const TAIL_WINDOW_MS = 3 * 60 * 1000;
/** Lead, so an event stamped slightly ahead of our clock is still inside the window. */
const TAIL_LEAD_MS = 60 * 1000;
/**
 * How often the whole window is re-read.
 *
 * This is not only about edits and deletions — it is what shows LONG events at
 * all. `/api/events/` filters by START time (verified: a window strictly inside
 * a 1252 s event returns nothing), and an event is published when it completes,
 * so a 20-minute idle block arrives with a start 20 minutes old — outside any
 * sane tail. Measured over 6 h on ES5503-2: only 0.18% of events run longer than
 * 3 minutes, but those hold 36% of the timeline's seconds. They are rare to
 * count and impossible to miss on screen, so the reconcile — not the tail — is
 * what puts the biggest segments up, and its interval is their worst-case
 * staleness.
 */
export const RECONCILE_MS = 30 * 1000;
/** The tail payload is small and superseded every poll; don't hold it. */
const TAIL_GC_MS = 60 * 1000;

/** Shared empty, so the whole-window path never hands out a fresh array. */
const EMPTY_RANGES: TimeRange[] = [];

export interface TimelineEventsResult {
  data: ApiResponse<MachineEvent[]> | undefined;
  status: "pending" | "error" | "success";
  dataUpdatedAt: number;
  isFetching: boolean;
  /**
   * Stretches of the requested window never fetched — what the rows should mark
   * as LOADING rather than as empty. Distinguishing the two is the whole point:
   * "nothing happened here" and "nobody has asked yet" look identical otherwise,
   * and only one of them is going to change.
   *
   * Always empty on the whole-window path, where one response covers everything.
   */
  pendingRanges: TimeRange[];
}

interface TimelineEventsOptions extends IEventQueryOptions {
  /** Realtime mode. Range mode is historical review and always uses one window. */
  live: boolean;
}

export const useTimelineEvents = (
  params: IGetEventsInput,
  options: TimelineEventsOptions,
): TimelineEventsResult => {
  const buffered = options.live && EVENT_BUFFER_ENABLED && !!params.device_mxid;
  const deviceMxid = params.device_mxid ?? "";
  const fromMs = params.start_time.getTime();
  const toMs = params.end_time.getTime();
  const mergeWindow = useEventBufferStore((state) => state.mergeWindow);

  // Legacy path. Disabled by blanking the device id — `useGetEvents` gates on
  // it — so both hooks are always called and hook order never changes.
  // `fetchAll`: this hook exists to back a TIMELINE, which draws a time range —
  // anything past the first page is a hole in that range, not a second screen
  // of results the operator can page to.
  const windowQuery = useGetEvents(
    { ...params, device_mxid: buffered ? "" : params.device_mxid },
    { ...options, fetchAll: true },
  );

  // Tail: stable key per camera, window computed at fetch time. A key holding
  // the rolling window mints a fresh cache entry every bucket and keeps each
  // payload for its gcTime; here the query is transport and the buffer is state.
  const tail = useQuery({
    queryKey: [QUERY_KEYS.EVENTS, "tail", deviceMxid],
    queryFn: async ({ signal }) => {
      const now = Date.now();
      const start = new Date(now - TAIL_WINDOW_MS);
      const end = new Date(now + TAIL_LEAD_MS);
      const response = await getEvents(
        { device_mxid: deviceMxid, start_time: start, end_time: end, page: 1, itemsPerPage: 2000 },
        signal,
      );
      return { from: start.getTime(), to: end.getTime(), events: response?.data ?? [] };
    },
    enabled: buffered,
    refetchInterval: options.refetchInterval ?? false,
    gcTime: TAIL_GC_MS,
    staleTime: 0,
  });

  // Reconcile: the full window, slowly. A tail can never show an edit or a
  // deletion further back, and `mergeWindow` treats a response as authoritative
  // for the window it covers — this is what makes them propagate.
  const reconcile = useQuery({
    queryKey: [QUERY_KEYS.EVENTS, "reconcile", deviceMxid],
    queryFn: async ({ signal }) => {
      const response = await getEvents(
        { device_mxid: deviceMxid, start_time: new Date(fromMs), end_time: new Date(toMs), page: 1, itemsPerPage: 20000 },
        signal,
      );
      return { from: fromMs, to: toMs, events: response?.data ?? [] };
    },
    enabled: buffered,
    refetchInterval: RECONCILE_MS,
    gcTime: TAIL_GC_MS,
    staleTime: 0,
  });

  useEffect(() => {
    if (!tail.data) return;
    mergeWindow(deviceMxid, tail.data.from, tail.data.to, tail.data.events);
  }, [tail.data, deviceMxid, mergeWindow]);

  useEffect(() => {
    if (!reconcile.data) return;
    mergeWindow(deviceMxid, reconcile.data.from, reconcile.data.to, reconcile.data.events);
  }, [reconcile.data, deviceMxid, mergeWindow]);

  // This camera's slice only. The merge preserves the array when nothing moved,
  // so consumers re-render on real change rather than on cadence.
  const records = useEventBufferStore((state) => state.devices[deviceMxid]);
  const coverage = useEventBufferStore((state) => state.coverage[deviceMxid]);

  // Recomputed from coverage rather than tracked separately, so it cannot drift
  // from what the buffer actually holds.
  const pendingRanges = useMemo(() => {
    // A cold start is not a GAP — it is an empty timeline, which the boot state
    // already speaks for. Reporting the whole window as pending here painted a
    // full-width loading band across every page load. Only once something has
    // been fetched does a hole in the middle mean "we have not asked about
    // this yet".
    if (!coverage?.length) return EMPTY_RANGES;
    // `coverage` is a dependency rather than an input: it is what changes when a
    // fetch lands, and `missingRanges` derives from the same state.
    return useEventBufferStore.getState().missingRanges(deviceMxid, fromMs, toMs);
  }, [coverage, deviceMxid, fromMs, toMs]);

  const bufferedEvents = useMemo(() => {
    if (!records) return undefined;
    return records
      .filter((record) => {
        const start = record.startMs;
        const end = start + (record.event.durationSeconds ?? 0) * 1000;
        // Overlap: an event that began before the window and is still running
        // belongs on screen.
        return start <= toMs && end >= fromMs;
      })
      .map((record) => record.event);
  }, [records, fromMs, toMs]);

  if (!buffered) {
    return {
      data: windowQuery.data,
      status: windowQuery.status,
      dataUpdatedAt: windowQuery.dataUpdatedAt,
      isFetching: windowQuery.isFetching,
      pendingRanges: EMPTY_RANGES,
    };
  }

  return {
    data: bufferedEvents ? ({ data: bufferedEvents } as ApiResponse<MachineEvent[]>) : undefined,
    status: bufferedEvents ? "success" : tail.status,
    // The chip asks "when did data last land", which is the tail's business.
    dataUpdatedAt: tail.dataUpdatedAt,
    isFetching: tail.isFetching,
    pendingRanges,
  };
};
